import { mapGetters } from 'vuex';
import {
  ENABLE_INVESTOR_DASHBOARD,
  FLOAT_DOWN_WORKFLOW,
  PRICE_EXCEPTION_APPROVER_QUEUE,
  PRICER_UI_LOCK_DESK_WORKFLOWS,
  PRICER_UI_QA_IS_MOBILE,
  PRICER_UI_REDESIGN_FRONTEND,
  RENEGOTIATION_WORKFLOW,
  WCP_ANALYSIS,
  NEXTGEN_ADMIN,
  NEXTGEN_PAR_RATE_CALCULATORS,
  ANALYTICS_DASHBOARDS_NATIVE,
} from '@shared/constants/flags';
import { DEFAULT_ROUTES } from '@pe/router';
import { clearCookie, getCookie } from '@shared/utils/cookies';
import { newAppBaseUrl, searchWithoutNewAppPort } from '@shared/utils/routing';

// frontend/src/pe/router/index.js
const LOAN_SCENARIOS_ROUTE_NAME = 'loan-scenarios';
const LOAN_PIPELINE_ROUTE_NAME = DEFAULT_ROUTES.loans;
const RATE_SHEETS_ROUTE_NAME = 'rate-sheets';
const LOAN_PRICING_ROUTE_NAME = 'loan-pricing';
const EMBEDDED_LOAN_SCENARIOS_ROUTE_NAME = 'loan-pricing-embed';
const EMBEDDED_LOAN_SCENARIOS_OPTIONS_ROUTE_NAME = 'loan-pricing-embed-options';
const RENEGOTIATION_ROUTE_NAME = 'renegotiation';
const FLOAT_DOWN_ROUTE_NAME = 'float-down';
const RELOCK_ANALYSIS_ROUTE_NAME = 'relock-analysis';
const RATE_ALERTS_NAME = 'rate-alerts';
const INVESTOR_STATUS = 'investor-status';
const LOCK_DESK_ROUTE_NAME = DEFAULT_ROUTES.lockDesk;
const SANDBOX_SYNC_HISTORY_ROUTE_NAME = 'sandbox-sync-history';
// frontend/src/useradmin/router/index.js — every route the nextgen_admin flag redirects.
const NEXTGEN_ADMIN_ROUTE_NAMES = [
  'useradmin',
  'notification-preferences',
  'account_profile',
  'account_membership',
  'users',
  'member_detail',
  'partnership',
];
// frontend/src/pe/router/index.js — the list route only. NextGen adds and edits
// inline, so the sibling new/details routes have no equivalent there; listing them
// would send users to a NextGen 404 rather than the working Classic page.
const PAR_RATE_CALCULATOR_ROUTE_NAMES = ['configure-par-rate-calculators'];
// frontend/src/analytics/router/index.js
const ANALYTICS_HOME_ROUTE_NAME = 'analyticsHome';

// List of old app routing names which we intend to redirect to the new app routes
// Ensure the defined old route includes meta field "newAppPath" (new app's path)
// this will allow us redirect to the new app route
// "newAppPath" implies /app
// e.g.) { [...], newAppPath: '/pe/new-loan-scenarios' }
// ends up being directed to '/app/pe/new-loan-scenarios'
const NEW_APP_REDIRECT_ROUTES = [
  LOAN_SCENARIOS_ROUTE_NAME,
  LOAN_PIPELINE_ROUTE_NAME,
  LOAN_PRICING_ROUTE_NAME,
  EMBEDDED_LOAN_SCENARIOS_ROUTE_NAME,
  EMBEDDED_LOAN_SCENARIOS_OPTIONS_ROUTE_NAME,
  RATE_ALERTS_NAME,
  INVESTOR_STATUS,
];
export default {
  data() {
    return {
      newAppLoading: false,
    };
  },
  computed: {
    ...mapGetters('core', [
      'isFlagActive',
      'organization',
      'orgPermissions',
      'orgUserPermissions',
      'isUserLockDeskOrHigher',
      'userInfo',
      'isAdmin',
      'isApprover',
      'isCapitalMarkets',
    ]),
    currentUserId() {
      return this.$store.getters['members/currentUserId'];
    },
    currentOrganizationId() {
      return this.$store.getters['organizations/currentOrganizationId'];
    },
  },
  watch: {
    $route: {
      immediate: true,
      handler: 'handleRouteRedirect',
    },
    userInfo: 'handleRouteForUserInfoChange',
    currentUserId: 'handleRouteForCurrentUserIdAndOrgId',
    currentOrganizationId: 'handleRouteForCurrentUserIdAndOrgId',
  },
  methods: {
    handleRouteForUserInfoChange(userInfo) {
      if (userInfo) {
        this.handleRouteRedirect(this.$route);
      }
    },
    handleRouteForCurrentUserIdAndOrgId() {
      if (this.currentUserId || this.currentOrganizationId) {
        this.handleRouteRedirect(this.$route);
      }
    },
    getAndValidateDefaultNewUiCookie() {
      const cookieData = getCookie('defaultNewUI') ?? {};

      const valid =
        cookieData?.orgId === this.currentOrganizationId &&
        cookieData?.userId === this.currentUserId;

      if (!valid) {
        clearCookie('defaultNewUI');
        return {};
      }

      return cookieData;
    },
    handleRouteRedirect(to) {
      const isStaff = this.userInfo?.is_staff;
      // info has not loaded yet
      if (
        isStaff === undefined ||
        this.currentUserId === undefined ||
        this.currentOrganizationId === undefined
      ) {
        return;
      }

      // NOTE: SPECIAL CASE FOR PRICER UI REDESIGN
      // redirect to new app UI
      const defaultNewUI = this.getAndValidateDefaultNewUiCookie();

      const forceUIRedirect = defaultNewUI['state'] === true && isStaff;
      const isRedirectRoute = NEW_APP_REDIRECT_ROUTES.includes(to.name);
      const hasRedesignAccess =
        this.isFlagActive(PRICER_UI_REDESIGN_FRONTEND) &&
        isRedirectRoute &&
        (forceUIRedirect ||
          (Object.keys(defaultNewUI).length === 0 &&
            (this.isFlagActive(PRICER_UI_QA_IS_MOBILE) || this.isMobile)));

      if (hasRedesignAccess || this.canAccessNewUiWithoutFlag(to.name)) {
        const { mode, type } = to.query;
        if (
          mode !== 'analysis' &&
          (mode !== 'lockDesk' ||
            (this.isFlagActive(PRICER_UI_LOCK_DESK_WORKFLOWS) &&
              this.isUserLockDeskOrHigher &&
              [
                'reprice',
                'relock',
                'productchange',
                'priceexception',
                'exception',
                'cancel',
                'cancellation',
                'extension',
              ].includes(type.toLowerCase())))
        ) {
          this.newAppLoading = true;
          const toPath = to.meta?.newAppPath
            ? this.replaceUrlParameter(to.meta.newAppPath, to.params)
            : to.path;

          // pass query params through, less the one that addressed this redirect
          location.replace(
            `${newAppBaseUrl()}${toPath}${searchWithoutNewAppPort(
              location.search,
            )}`,
          );
        }
      }
    },
    canAccessRateSheets(routeName) {
      return (
        this.organization.enable_custom_ratesheets &&
        routeName === RATE_SHEETS_ROUTE_NAME
      );
    },
    canAccessRenegotiation(routeName) {
      return (
        this.isFlagActive(RENEGOTIATION_WORKFLOW) &&
        routeName === RENEGOTIATION_ROUTE_NAME
      );
    },
    canAccessFloatDown(routeName) {
      return (
        this.isFlagActive(FLOAT_DOWN_WORKFLOW) &&
        routeName === FLOAT_DOWN_ROUTE_NAME
      );
    },
    canAccessRelockAnalysis(routeName) {
      return (
        this.isFlagActive(WCP_ANALYSIS) &&
        routeName === RELOCK_ANALYSIS_ROUTE_NAME
      );
    },
    canAccessInvestorStatus(routeName) {
      return (
        this.isFlagActive(ENABLE_INVESTOR_DASHBOARD) &&
        routeName === INVESTOR_STATUS
      );
    },
    // Either sandbox role may view sync history: admins manage the environments,
    // resource-sync users produce the syncs the page lists.
    canAccessSandboxSyncHistory(routeName) {
      const canViewSyncHistory =
        (this.orgUserPermissions.can_access_sandbox ?? false) ||
        (this.orgUserPermissions.can_sync_sandbox_resources ?? false);
      return (
        canViewSyncHistory && routeName === SANDBOX_SYNC_HISTORY_ROUTE_NAME
      );
    },
    // One flag covers the whole useradmin section, so every migrated page shares this helper
    // rather than getting one apiece — an org is never split across the two UIs.
    canAccessNextGenAdmin(routeName) {
      return (
        this.isFlagActive(NEXTGEN_ADMIN) &&
        NEXTGEN_ADMIN_ROUTE_NAMES.includes(routeName)
      );
    },
    // Joined to the per-feature chain rather than NEW_APP_REDIRECT_ROUTES, which
    // additionally requires isMobile: par rate calculators is a desktop admin
    // screen, so that path would never fire for its actual audience.
    canAccessParRateCalculators(routeName) {
      return (
        this.isFlagActive(NEXTGEN_PAR_RATE_CALCULATORS) &&
        PAR_RATE_CALCULATOR_ROUTE_NAMES.includes(routeName)
      );
    },
    // Must check the same flag as analyticsHome's meta.newAppFlag, which gates the SideNav switcher.
    canAccessAnalyticsDashboardsNative(routeName) {
      return (
        this.isFlagActive(ANALYTICS_DASHBOARDS_NATIVE) &&
        routeName === ANALYTICS_HOME_ROUTE_NAME
      );
    },
    // Deliberately approver-view only (APP-32695 soft launch).
    canAccessPriceExceptionQueue(routeName) {
      return (
        this.isFlagActive(PRICE_EXCEPTION_APPROVER_QUEUE) &&
        routeName === LOCK_DESK_ROUTE_NAME &&
        (this.orgPermissions?.has_price_exception_management_enabled ??
          false) &&
        this.isApprover &&
        !this.isAdmin
      );
    },
    canAccessNewUiWithoutFlag(routeName) {
      return (
        this.canAccessRateSheets(routeName) ||
        this.canAccessRenegotiation(routeName) ||
        this.canAccessFloatDown(routeName) ||
        this.canAccessRelockAnalysis(routeName) ||
        this.canAccessInvestorStatus(routeName) ||
        this.canAccessSandboxSyncHistory(routeName) ||
        this.canAccessNextGenAdmin(routeName) ||
        this.canAccessParRateCalculators(routeName) ||
        this.canAccessAnalyticsDashboardsNative(routeName) ||
        this.canAccessPriceExceptionQueue(routeName)
      );
    },
    replaceUrlParameter(url, params) {
      return url.replace(/:(\w+)/g, (match, key) => {
        return params[key] !== undefined ? params[key] : match;
      });
    },
  },
};
